import { getPhaseFromCollection, getPhaseProjectId, phaseCollection } from 'collections/phase';
import { getProjectFromCollection, projectCollection } from 'collections/project';
import { taskCollection } from 'collections/tasks';
import { planBoardPhaseCollection } from './plan-lane-collection';
const snapshots = new Map();
/**
 * Ids whose CREATION has actually been counted (+1 applied), as opposed to
 * merely baselined for future diffing. Distinct from `snapshots.has(id)` —
 * every task the reconciler sees at all gets a baseline snapshot (including
 * ordinary loads, which must NOT count), so `snapshots.has(id)` being true
 * does not mean "already counted". `recordTaskCreatedInPhase` needs its own
 * idempotency guard that isn't satisfied by the passive baseline the generic
 * `origin: 'push'` handling always records first.
 */ const explicitlyCountedCreations = new Set();
const toSnapshot = (task)=>{
    var _task_fields, _ref;
    var _find, _task_projectPhase;
    // Convention shared with getTaskCounter (use-project-phase.store.ts): the
    // Status field is the one whose metaFieldValue carries a status `group`.
    const status = (_find = ((_task_fields = task.fields) !== null && _task_fields !== void 0 ? _task_fields : []).find((field)=>{
        var _field_metaFieldValue;
        return (field === null || field === void 0 ? void 0 : (_field_metaFieldValue = field.metaFieldValue) === null || _field_metaFieldValue === void 0 ? void 0 : _field_metaFieldValue.group) != null;
    })) === null || _find === void 0 ? void 0 : _find.metaFieldValue;
    const project = task.project;
    return {
        projectId: typeof project === 'number' ? project : (_ref = project === null || project === void 0 ? void 0 : project.projectId) !== null && _ref !== void 0 ? _ref : undefined,
        phaseId: (_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId,
        statusValue: status === null || status === void 0 ? void 0 : status.value,
        group: status === null || status === void 0 ? void 0 : status.group,
        label: status === null || status === void 0 ? void 0 : status.label,
        color: status === null || status === void 0 ? void 0 : status.color
    };
};
const sameCounting = (a, b)=>a.phaseId === b.phaseId && a.statusValue === b.statusValue;
/**
 * Apply a ±1 to the progressStatus entry matching the snapshot's status
 * option. Increments append a fresh entry when the option isn't present yet
 * (first task of that status in the phase); decrements clamp at zero and keep
 * the entry (a zero-count group renders fine).
 */ const applyProgressDelta = (progressStatus, snapshot, delta)=>{
    if (snapshot.statusValue == null || snapshot.group == null) {
        return progressStatus;
    }
    const entries = progressStatus !== null && progressStatus !== void 0 ? progressStatus : [];
    const index = entries.findIndex((entry)=>entry.value === snapshot.statusValue);
    if (index === -1) {
        var _snapshot_label, _snapshot_color;
        if (delta < 0) {
            return progressStatus;
        }
        return [
            ...entries,
            {
                key: String(snapshot.statusValue),
                value: snapshot.statusValue,
                label: (_snapshot_label = snapshot.label) !== null && _snapshot_label !== void 0 ? _snapshot_label : '',
                color: (_snapshot_color = snapshot.color) !== null && _snapshot_color !== void 0 ? _snapshot_color : '',
                group: snapshot.group,
                count: 1
            }
        ];
    }
    return entries.map((entry, i)=>{
        var _entry_count;
        return i === index ? {
            ...entry,
            count: Math.max(0, Number((_entry_count = entry.count) !== null && _entry_count !== void 0 ? _entry_count : 0) + delta)
        } : entry;
    });
};
const patchPhaseAggregates = (snapshot, delta, totalDelta, /**
   * `'local'` (default) for status transitions, deletions, and cross-phase
   * moves: the underlying task entity ALSO broadcasts (a `.update()`/
   * `.delete()` transaction always does), so every peer tab's own reconciler
   * independently recomputes the SAME delta from that synced task — no need
   * to also broadcast the aggregate write, and doing so would double-apply
   * it on every peer.
   *
   * `'mutation'` for genuine creations ONLY: a peer's reconciler sees a
   * synced new task arrive as `origin: 'push'` (indistinguishable from a
   * lane/detail load bringing in a task the server aggregate already
   * counts — see the create-vs-load handling below) and deliberately does
   * NOT count it. Nothing else derives this delta on a peer, so it must be
   * broadcast explicitly or peer tabs never learn a task was created at all
   * (the project-overview "All phases" card open in another tab stays frozen).
   */ source = 'local')=>{
    var _ref, _snapshot_projectId;
    var _planBoardPhaseCollection_get, _project_projectPhases;
    const { phaseId } = snapshot;
    if (phaseId == null) {
        return;
    }
    // Home 1 — the phase bean itself (drives paginatedCounters in lanes via
    // `usePhase`). One write here now covers every board/lane reader —
    // `planBoardPhaseCollection` no longer carries a `.phase` copy to keep in
    // sync (state-db-migration-phase.md Wave 2).
    const phase = getPhaseFromCollection(phaseId);
    if (phase) {
        var _phase_aggregateProgressValues;
        phaseCollection.push([
            {
                ...phase,
                progressStatus: applyProgressDelta(phase.progressStatus, snapshot, delta),
                ...phase.totalTaskCount != null && {
                    totalTaskCount: Math.max(0, phase.totalTaskCount + totalDelta)
                },
                // `aggregateProgressValues.taskCount` is the same total-task count
                // as `totalTaskCount`, just on the legacy aggregate shape — move it
                // by the same delta so it never drifts from the field it duplicates.
                ...((_phase_aggregateProgressValues = phase.aggregateProgressValues) === null || _phase_aggregateProgressValues === void 0 ? void 0 : _phase_aggregateProgressValues.taskCount) != null && {
                    aggregateProgressValues: {
                        ...phase.aggregateProgressValues,
                        taskCount: Math.max(0, phase.aggregateProgressValues.taskCount + totalDelta)
                    }
                }
            }
        ], {
            source
        });
    }
    // Home 2 — the phase inside the project detail (overview widgets, legacy
    // fallback `boardPhase ?? projectPhase` in the lane header, and every other
    // reader not yet flipped to `usePhase`/`usePhasesByProject`).
    const projectId = (_ref = (_snapshot_projectId = snapshot.projectId) !== null && _snapshot_projectId !== void 0 ? _snapshot_projectId : phase && getPhaseProjectId(phase)) !== null && _ref !== void 0 ? _ref : (_planBoardPhaseCollection_get = planBoardPhaseCollection.get(phaseId)) === null || _planBoardPhaseCollection_get === void 0 ? void 0 : _planBoardPhaseCollection_get.projectId;
    const project = projectId != null ? getProjectFromCollection(projectId) : undefined;
    if (project === null || project === void 0 ? void 0 : (_project_projectPhases = project.projectPhases) === null || _project_projectPhases === void 0 ? void 0 : _project_projectPhases.some((p)=>p.projectPhaseId === phaseId)) {
        projectCollection.push([
            {
                ...project,
                projectPhases: project.projectPhases.map((detailPhase)=>{
                    var _detailPhase_aggregateProgressValues;
                    return detailPhase.projectPhaseId === phaseId ? {
                        ...detailPhase,
                        progressStatus: applyProgressDelta(detailPhase.progressStatus, snapshot, delta),
                        ...detailPhase.totalTaskCount != null && {
                            totalTaskCount: Math.max(0, detailPhase.totalTaskCount + totalDelta)
                        },
                        ...((_detailPhase_aggregateProgressValues = detailPhase.aggregateProgressValues) === null || _detailPhase_aggregateProgressValues === void 0 ? void 0 : _detailPhase_aggregateProgressValues.taskCount) != null && {
                            aggregateProgressValues: {
                                ...detailPhase.aggregateProgressValues,
                                taskCount: Math.max(0, detailPhase.aggregateProgressValues.taskCount + totalDelta)
                            }
                        }
                    } : detailPhase;
                })
            }
        ], {
            source
        });
    }
};
let reconcilerAttached = false;
export const attachPhaseProgressReconciler = ()=>{
    if (reconcilerAttached) {
        return;
    }
    reconcilerAttached = true;
    taskCollection.subscribeToWrites((event)=>{
        var _event_created;
        // `purge`/`clear` are local memory management, never a statement about
        // server state — must not be read as deletions.
        if (event.origin === 'purge' || event.origin === 'clear') {
            return;
        }
        const createdIds = ((_event_created = event.created) !== null && _event_created !== void 0 ? _event_created : []).filter((id)=>typeof id === 'number');
        const deletedIds = event.deletes.filter((id)=>typeof id === 'number');
        const createdSet = new Set(createdIds);
        // `createTaskOptimistic` inserts under a temp negative id, then the
        // server-commit swaps it for the real id — BOTH land in this one event
        // (`deletes: [tempId], created: [realId]`). Pair them index-wise (the
        // only creation path in this codebase is a single `taskCollection.insert`
        // per call, so `created`/`deletes` are each at most length 1 here) and
        // TRANSFER the snapshot instead of treating the temp-id removal as a
        // real delete — that misreading is exactly what silently decremented
        // `totalTaskCount` by one on every task creation before this fix.
        const swapCount = Math.min(createdIds.length, deletedIds.length);
        for(let i = 0; i < swapCount; i += 1){
            const oldId = deletedIds[i];
            const newId = createdIds[i];
            const previous = snapshots.get(oldId);
            snapshots.delete(oldId);
            const task = taskCollection.get(newId);
            const next = task ? toSnapshot(task) : previous;
            if (!next) {
                continue;
            }
            snapshots.set(newId, next);
            // Already counted at the temp id's `origin: 'optimistic'` moment below —
            // only apply a delta if the server response actually changed something
            // count-relevant (e.g. resolved a different phase than the draft guessed).
            if (previous && !sameCounting(previous, next)) {
                const phaseChanged = previous.phaseId !== next.phaseId;
                patchPhaseAggregates(previous, -1, phaseChanged ? -1 : 0);
                patchPhaseAggregates(next, 1, phaseChanged ? 1 : 0);
            }
        }
        // Genuine new tasks, not absorbed by a swap above.
        createdIds.slice(swapCount).forEach((id)=>{
            const task = taskCollection.get(id);
            if (!task || snapshots.has(id)) {
                return;
            }
            // `origin: 'push', source: 'local'` is an EPHEMERAL client-side
            // placeholder (`queries/task.ts`'s plan-board "add task" flow pushes a
            // temp task under its own negative id, then `externalDelete`s it once
            // the real, server-assigned id lands — see `recordTaskCreatedInPhase`).
            // Do NOT baseline it: if it were baselined, its later `externalDelete`
            // would hit the "genuine deletes" branch below and read as a REAL
            // deletion, silently decrementing a count that was never incremented
            // for it in the first place.
            if (event.origin === 'push' && event.source === 'local') {
                return;
            }
            const next = toSnapshot(task);
            // `origin: 'push'` (a lane/detail fetch, or the real-id push from this
            // bespoke create flow's `createTaskRecord`) can't distinguish a task the
            // server aggregate already counts from a genuinely new one — baseline
            // only. `recordTaskCreatedInPhase` is what counts THAT specific case
            // explicitly, with its own idempotency guard. Every OTHER origin
            // reaching this branch (`'optimistic'` insert, or a `'mutation'` commit
            // with no temp-id precursor) is a real, first-count moment.
            snapshots.set(id, next);
            if (event.origin !== 'push') {
                explicitlyCountedCreations.add(id);
                // Broadcast: a peer's reconciler can't derive this delta on its own
                // (the synced task arrives there as `origin: 'push'`, deliberately
                // baseline-only) — see `patchPhaseAggregates`'s `source` doc.
                patchPhaseAggregates(next, 1, 1, 'mutation');
            }
        });
        // Genuine deletes, not absorbed by a swap above.
        deletedIds.slice(swapCount).forEach((id)=>{
            const previous = snapshots.get(id);
            if (previous) {
                patchPhaseAggregates(previous, -1, -1);
                snapshots.delete(id);
                explicitlyCountedCreations.delete(id);
            }
        });
        // Ordinary edits: upserts that are neither a fresh creation nor already
        // handled by the swap pairing — diff against the recorded snapshot and
        // apply the transition delta.
        event.upserts.forEach((task)=>{
            const id = task === null || task === void 0 ? void 0 : task.taskId;
            if (typeof id !== 'number' || createdSet.has(id)) {
                return;
            }
            const previous = snapshots.get(id);
            const next = toSnapshot(task);
            if (!previous) {
                // First sight outside of a tracked creation (e.g. a lane/detail load
                // surfacing a task this tab never held before) — baseline only, the
                // server aggregate already counts it.
                snapshots.set(id, next);
                return;
            }
            if (!sameCounting(previous, next)) {
                const phaseChanged = previous.phaseId !== next.phaseId;
                // Same phase: totals unchanged, only the group shifts.
                // Cross-phase: the task's whole contribution moves.
                patchPhaseAggregates(previous, -1, phaseChanged ? -1 : 0);
                patchPhaseAggregates(next, 1, phaseChanged ? 1 : 0);
            }
            snapshots.set(id, next);
        });
    });
};
/**
 * Count a task created OUTSIDE this reconciler's own insert-based detection.
 *
 * `queries/task.ts`'s plan-board "add task" flow manages its own temp-id
 * lifecycle by hand (an optimistic `taskCollection.push`, then
 * `externalDelete` of the temp id once the real one settles — for
 * card-continuity reasons, see TC-1107) instead of `taskCollection.insert`.
 * This reconciler's `subscribeToWrites` sees that push as `origin: 'push'`,
 * indistinguishable from a lane page loading a task the server aggregate
 * already counts — so it correctly-but-wrongly treats it as a load, never a
 * creation, and `progressStatus`/`totalTaskCount` never move.
 *
 * Call this once the create SETTLES, with the server-confirmed task (the
 * REAL id — never the temp one, which gets `externalDelete`d and must not be
 * registered here, or its cleanup would read as a genuine deletion and
 * decrement the count that was never incremented for it in the first place).
 * Recording the baseline here also means a LATER status change to this task
 * is diffed correctly by the normal `subscribeToWrites` path instead of
 * silently re-baselining (which would swallow that transition too).
 */ export const recordTaskCreatedInPhase = (task)=>{
    const id = task === null || task === void 0 ? void 0 : task.taskId;
    // NOT `snapshots.has(id)`: by the time this runs, `createTaskRecord`'s own
    // `taskCollection.push([task], { source: 'mutation' })` has ALREADY fired
    // this reconciler's `subscribeToWrites` listener synchronously, which
    // baselines the id (the generic `origin: 'push'` handling — correctly, it
    // can't tell this apart from an ordinary load). `snapshots.has(id)` would
    // therefore already be true and this call would silently no-op every time,
    // never counting the creation at all. `explicitlyCountedCreations` is a
    // separate idempotency guard for exactly this call, not shared with the
    // passive baseline.
    if (typeof id !== 'number' || explicitlyCountedCreations.has(id)) {
        return;
    }
    explicitlyCountedCreations.add(id);
    const next = toSnapshot(task);
    snapshots.set(id, next);
    // Broadcast — see `patchPhaseAggregates`'s `source` doc: a peer's own
    // reconciler can't derive this delta (this creation path's task push
    // arrives there as an ordinary, deliberately-uncounted load).
    patchPhaseAggregates(next, 1, 1, 'mutation');
};
/** Test hook: forget every baseline (module-level singletons leak state). */ export const resetPhaseProgressSnapshots = ()=>{
    snapshots.clear();
    explicitlyCountedCreations.clear();
};
